import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import type { AgingReport, PaginatedResponse } from './usePayables';

/**
 * Tipos espelhando `ReceivableResponseDto`, campo a campo.
 *
 * Mesma história de contas a pagar: a tela declarava `clienteNome`, `valor`,
 * `dataVencimento`, `descricao`, `diasAtraso`, `valorRecebido` — e o backend
 * manda `customerName`, `openBalance`, `dueDate`, `documentNumber`,
 * `receivedAmount`. Com dados reais, a listagem aparecia em branco.
 */

export type ReceivableStatus =
  | 'OPEN'
  | 'PARTIALLY_SETTLED'
  | 'SETTLED'
  | 'OVERDUE'
  | 'CANCELLED'
  | 'RENEGOTIATED';

export type ReceivableOrigin = 'SALE' | 'SERVICE' | 'RENT' | 'INTEREST' | 'OTHER';

export interface Receivable {
  id: string;
  customerId: string;
  customerName: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string;
  originalAmount: number;
  /** Saldo em aberto: é o valor que a tela cobra. */
  openBalance: number;
  receivedAmount: number;
  status: ReceivableStatus;
  category: string;
  costCenterId?: string;
  origin: ReceivableOrigin;
  notes?: string | null;
  paymentMethod?: string | null;
  interestAmount: number;
  fineAmount: number;
  discountAmount: number;
  receivedAt?: string | null;
  installmentPlanId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReceivableInput {
  customerId: string;
  customerName: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string;
  originalAmount: number;
  category: string;
  costCenterId?: string;
  origin?: ReceivableOrigin;
  notes?: string;
}

/** `SettleReceivableDto`: a baixa entra em conta, então conta e método são obrigatórios. */
export interface SettleInput {
  amount: number;
  receivedDate: string;
  paymentMethod: string;
  accountId: string;
  notes?: string;
}

export interface RenegotiateInput {
  installments: { amount: number; dueDate: string }[];
  reason: string;
}

export interface SettlementResult {
  transactionId: string;
  entityId: string;
  newBalance: number;
  newStatus: string;
  settledAmount: number;
}

export type { AgingReport, PaginatedResponse };

/** Dias de atraso na data de hoje — derivado; o backend não manda o número. */
export function daysOverdue(receivable: Pick<Receivable, 'dueDate' | 'status'>): number {
  if (receivable.status === 'SETTLED' || receivable.status === 'CANCELLED') return 0;
  const due = new Date(receivable.dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  const diff = Math.floor((Date.now() - due.getTime()) / 86_400_000);
  return diff > 0 ? diff : 0;
}

export function useReceivables(params?: {
  status?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: [...queryKeys.finance.receivables(), params],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Receivable>>('/finance/receivables', {
        params: {
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.startDate ? { startDate: params.startDate } : {}),
          ...(params?.endDate ? { endDate: params.endDate } : {}),
        },
      }),
  });
}

export function useReceivablesAging() {
  return useQuery({
    queryKey: [...queryKeys.finance.receivables(), 'aging'],
    queryFn: () => apiClient.get<AgingReport>('/finance/receivables/aging'),
  });
}

export function useCreateReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceivableInput) =>
      apiClient.post<Receivable>('/finance/receivables', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.receivables() });
    },
  });
}

export function useUpdateReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<ReceivableInput> & { id: string }) =>
      apiClient.put<Receivable>(`/finance/receivables/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.receivables() });
    },
  });
}

export function useSettleReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: SettleInput & { id: string }) =>
      apiClient.post<SettlementResult>(`/finance/receivables/${id}/settle`, {
        receivableId: id,
        ...input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.receivables() });
    },
  });
}

export function useRenegotiateReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: RenegotiateInput & { id: string }) =>
      apiClient.post<{ originalReceivable: Receivable; installments: Receivable[] }>(
        `/finance/receivables/${id}/renegotiate`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.receivables() });
    },
  });
}

export function useDeleteReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/finance/receivables/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.receivables() });
    },
  });
}
